import React, { useState, useEffect } from "react";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import { Settings as SettingsIcon, GridView as GridViewIcon, ViewList as ViewListIcon } from "@mui/icons-material";
import Setup from "./components/Setup";
import Permission from "./components/Permission";
import Calendar from "./components/Calendar";
import GridView from "./components/GridView";
import List from "./components/List";
import AppointmentReport from "./components/AppointmentReport";
import SettingsView from "./components/SettingsView";
import { checkPermission } from "../common/Sidebar/checkPermission";
import { getCalendarSettings } from "./api/actions/query";

const GoogleCalendarEmbed = () => {
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("calendarViewMode") || "calendar";
  }); // 'calendar' or 'list'
  const [showSettings, setShowSettings] = useState(false);
  const [permissions, setPermissions] = useState({
    canEdit: false,
    canDelete: false,
    canCreate: false,
    canView: false,
  });
  const [roleName, setRoleName] = useState("");
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  const fetchCalendarSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const response = await getCalendarSettings();
      setCalendarSettings(response || null);
    } catch (error) {
      console.error("Error fetching calendar settings:", error);
      setCalendarSettings(null);
    }
    setIsLoadingSettings(false);
  };

  useEffect(() => {
    const verifyPermissions = async () => {
      const modulePermission = await checkPermission("engagement", "calendar");
      setPermissions({
        canEdit: !!modulePermission?.canEdit,
        canDelete: !!modulePermission?.canDelete,
        canCreate: !!modulePermission?.canCreate,
        canView: !!modulePermission?.canView,
      });
    };

    const loadRoleName = () => {
      try {
        const userRole = localStorage.getItem("user");
        if (userRole) {
          const parsedUser = JSON.parse(userRole);
          setRoleName(parsedUser.roleDetails?.name || "");
        }
      } catch (error) {
        console.error("Error loading role:", error);
      }
    };

    loadRoleName();
    verifyPermissions();
    fetchCalendarSettings();
  }, []);

  if (isLoadingSettings) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If settings view is active, show the consolidated settings page
  if (showSettings) {
    return (
      <SettingsView
        onBack={() => {
          setShowSettings(false);
          fetchCalendarSettings();
        }}
        permissions={permissions}
        calendarSettings={calendarSettings}
        onSettingsUpdate={setCalendarSettings}
        roleName={roleName}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      {/* Report Section - Fixed at the top */}
      <Box sx={{ px: 3, pt: 2, backgroundColor: "#fff" }}>
        <AppointmentReport />
      </Box>

      {/* Header Bar - Below Report */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        py: 1,
        backgroundColor: "#fff",
        //borderTop: "1px solid #e0e0e0",
        //borderBottom: "1px solid #e0e0e0",
        //boxShadow: "0px 1px 3px rgba(0,0,0,0.1)"
      }}>
        {/* View Switcher */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant={viewMode === "calendar" ? "contained" : "outlined"}
            onClick={() => {
              setViewMode("calendar");
              localStorage.setItem("calendarViewMode", "calendar");
            }}
            startIcon={<GridViewIcon />}
            size="small"
            sx={{
              textTransform: "none",
              backgroundColor: viewMode === "calendar" ? "#ED6C02" : "transparent",
              color: viewMode === "calendar" ? "#fff" : "#0D3559",
              borderColor: "#0D3559",
              "&:hover": {
                backgroundColor: viewMode === "calendar" ? "#d96002" : "rgba(13, 53, 89, 0.04)",
                borderColor: "#0D3559",
              }
            }}
          >
            Grid View
          </Button>
          <Button
            variant={viewMode === "list" ? "contained" : "outlined"}
            onClick={() => {
              setViewMode("list");
              localStorage.setItem("calendarViewMode", "list");
            }}
            startIcon={<ViewListIcon />}
            size="small"
            sx={{
              textTransform: "none",
              backgroundColor: viewMode === "list" ? "#ED6C02" : "transparent",
              color: viewMode === "list" ? "#fff" : "#0D3559",
              borderColor: "#0D3559",
              "&:hover": {
                backgroundColor: viewMode === "list" ? "#d96002" : "rgba(13, 53, 89, 0.04)",
                borderColor: "#0D3559",
              }
            }}
          >
            List View
          </Button>
        </Box>

        {/* Settings Icon */}
        {permissions.canView && (
          <IconButton
            onClick={() => setShowSettings(true)}
            title="Settings"
            sx={{ color: '#0d3559' }}
          >
            <SettingsIcon />
          </IconButton>
        )}
      </Box>

      {/* Main Content Area */}
      <Box sx={{ p: 3, pt: 2 }}>
        {viewMode === "calendar" ? (
          calendarSettings?.calendarProvider === 'outlook' ? (
            <GridView settings={calendarSettings} />
          ) : (
            <Calendar initialSettings={calendarSettings} />
          )
        ) : (
          // List View always shows details table
          <List key="main-list" canDelete={permissions.canDelete} initialFilter="all_details" />
        )}
      </Box>
    </Box>
  );
};

export default GoogleCalendarEmbed;
